import React from "react";
import {useSearchParams} from "react-router-dom";
import {useSetting} from "../../contexts/setting";
import cn from "classnames";
import "./index.css";
import {UI} from "../../data/constants";
import {track} from "libs/analytics";
import {useSceneScope} from "hooks/useSceneScope";
import LivestreamLayoutIcon, {hasLivestreamLayoutIcon} from "./LivestreamLayoutIcon";
import GdnLayoutIcon, {hasGdnLayoutIcon} from "./GdnLayoutIcon";
import AirLayoutIcon from "./AirLayoutIcon";
import ScreenshotLayoutIcon, {hasScreenshotLayoutIcon} from "./ScreenshotLayoutIcon";
import {variantAllowsLayout} from "data/products/index";

/**
 * The icon of one layout button. Livestream uses inline photo-count icons; other
 * channels load their thumbnails from the CDN by iconId. The icon set belongs to
 * the offering, so a product that carries its own (JetBrains Air, whose
 * Screenshot set numbers its icons 20-27) finds them through either channel
 * that lists it. `iconChannel` names the `/Layouts/<channel>/` folder of the
 * CDN icons. Returns `inline` (an SVG element) or `src` (a CDN url).
 */
export function layoutIconFor({channelValue, typeValue, iconChannel, option}) {
  const id = Number(option.id);
  if (channelValue === "livestream" && hasLivestreamLayoutIcon(id)) {
    return {inline: <LivestreamLayoutIcon id={id} />};
  }
  if (channelValue === "ads" && typeValue === "google-display-network" && hasGdnLayoutIcon(id)) {
    return {inline: <GdnLayoutIcon id={id} />, isGdn: true};
  }
  if (channelValue === "releases" && hasScreenshotLayoutIcon(id)) {
    return {inline: <ScreenshotLayoutIcon id={id} />};
  }
  return {src: `${UI}/Layouts/${iconChannel}/${option.iconId}.svg`};
}

const Button = ({value, handleClick, src, isCurrent, isGdn, style, children, ...rest}) => {
  const btnClass = cn("layout-button", {
    selected: isCurrent,
    "layout-button--gdn": isGdn,
  });
  return (
    <button
      value={value}
      className={btnClass}
      onClick={() => handleClick(value)}
      // Inline-icon buttons render their SVG as a child; CDN-icon buttons use a
      // background image. Don't set both — an undefined `url()` would 404.
      style={children ? style : {backgroundImage: `url(${src})`, ...style}}
      {...rest}
    >
      {children}
    </button>
  );
};

/**
 * The layout × variant grid: one cell per (layout, brand treatment) pair, rows
 * by layout, columns by treatment. The icons carry both axes, so the grid has
 * no captions. A cell writes both values in one step, so the user picks a
 * look, not two settings. Each cell keeps the `.layout-button[value=<layout>]`
 * contract of the flat row and adds `data-variant`.
 *
 * Only JetBrains Air declares variants, so the cells draw AirLayoutIcon; a
 * second product with treatments will need an icon set of its own here.
 *
 * A row is not always full: a treatment the design draws for some layouts only
 * declares them (`variantAllowsLayout`), and the missing pair leaves a gap.
 */
const LayoutMatrix = ({options, variants, onCellClick}) => {
  const {layoutOption, variantOption} = useSetting();
  return (
    <div
      className="layout-selector layout-selector--matrix"
      style={{"--variant-count": variants.length}}
    >
      {options.map((opt) =>
        variants.map((v, column) =>
          // A treatment may be drawn for some layouts only. The pair the
          // design never drew is no cell; its column stays empty, so the rest
          // of the row keeps its place under the treatment it belongs to.
          variantAllowsLayout(v, opt.id) ? (
            <Button
              key={`${opt.id}-${v.id}`}
              value={opt.id}
              data-variant={v.id}
              title={v.hint}
              style={{gridColumn: column + 1}}
              handleClick={() => onCellClick(opt.id, v.id)}
              isCurrent={layoutOption === opt.id && Number(variantOption) === v.id}
            >
              <AirLayoutIcon layoutId={Number(opt.id)} variant={v.id} />
            </Button>
          ) : null
        )
      )}
    </div>
  );
};

/**
 * Renders one button per entry in `options`. Each option is `{id, iconId}`:
 * `id` is the canonical layout identifier (written to the URL) and `iconId`
 * is the SVG filename served from the `/Layouts/<channel>/<iconId>.svg` CDN
 * path. Keeping them separate lets sparse or aliased layout sets exist
 * without the button value drifting from the displayed icon (issue #80).
 *
 * Callers must pre-resolve `options` through `resolveLayoutOptions` so the
 * policy-layer overrides in `data/layoutPolicy.js` are applied uniformly.
 *
 * `variants` turns the flat row into the layout × variant grid (LayoutMatrix)
 * for a product whose banner ships in several brand treatments.
 *
 * `iconChannel` names the `/Layouts/<channel>/` folder of the icons. The
 * caller passes the set's own folder when the set shows another channel's
 * banners; otherwise it is the channel that holds the data (`dataChannel`).
 *
 * `footer` is an optional slot rendered inside the panel box, below the icon
 * grid and separated by a rule. It holds controls that belong with the layout
 * choice; the caller owns what goes there, so this component stays generic.
 */
const LayoutSelector = ({options, iconChannel, variants, footer}) => {
  const [, setSearchParams] = useSearchParams();
  const {layoutOption, variantOption, channel, type} = useSetting();

  const scope = useSceneScope();
  if (!iconChannel) throw new Error("LayoutSelector: iconChannel is required");
  // A click on the button that is already chosen changes nothing. On a scopes
  // channel a shared click also drops the layouts that banners have from the
  // defaults (`yieldPresets`), so a second click on the chosen layout put
  // SoMe Square of RustRover on a layout its cut is not made for (2026-11-03).
  // Under a selection the click gives the selected banners that layout, so it
  // is not empty there. The comparisons are the ones of `isCurrent`.
  const isChosen = (layout, variant) =>
    !scope.isSelection &&
    layoutOption === layout &&
    (variant === undefined || Number(variantOption) === variant);
  const onCellClick = (layout, variant) => {
    if (isChosen(layout, variant)) return;
    track("neurobaker.layout.selected", {layout, variant});
    if (scope.scoped) {
      scope.applyLayoutVariant(layout, variant);
      return;
    }
    // One updater for both keys: two functional updates in one handler each
    // start from the params this render closed over, and the second wins.
    setSearchParams(
      (params) => {
        params.set("layout", String(layout));
        params.set("variant", String(variant));
        return params;
      },
      {replace: true}
    );
  };
  const onLayoutButtonClick = (value) => {
    if (isChosen(value)) return;
    track("neurobaker.layout.selected", {layout: value});
    // On a scopes channel the hook decides: the selection gets layouts of
    // their own, everyone else goes through the URL as before.
    if (scope.scoped) {
      scope.applyLayout(value);
      return;
    }
    setSearchParams(
      (params) => {
        params.set("layout", value);
        return params;
      },
      {replace: true}
    );
  };

  if (variants?.length) {
    return (
      <div className="layout-panel">
        <LayoutMatrix options={options} variants={variants} onCellClick={onCellClick} />
        {footer ? <div className="layout-panel__footer">{footer}</div> : null}
      </div>
    );
  }

  return (
    <div className="layout-panel">
      <div className="layout-selector">
        {options.map((opt) => {
          const icon = layoutIconFor({
            channelValue: channel.value,
            typeValue: type.value,
            iconChannel,
            option: opt,
          });
          return (
            <Button
              key={opt.id}
              value={opt.id}
              src={icon.src}
              handleClick={onLayoutButtonClick}
              isCurrent={layoutOption === opt.id}
              isGdn={icon.isGdn}
            >
              {icon.inline}
            </Button>
          );
        })}
      </div>
      {footer ? <div className="layout-panel__footer">{footer}</div> : null}
    </div>
  );
};

export default LayoutSelector;
